import { useState } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsDown,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { ColoanaTabel, RandRaport } from "@/utils/targetareTabel";

type DialogColoaneProps<R extends RandRaport> = {
  deschis: boolean;
  toateColoanele: ColoanaTabel<R>[];
  coloane: string[];
  onInchide: () => void;
  onAplica: (coloane: string[]) => void;
};

function Lista({
  titlu,
  elemente,
  eticheta,
  selectat,
  onSelect,
}: {
  titlu: string;
  elemente: string[];
  eticheta: (id: string) => string;
  selectat: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="flex-1">
      <p className="mb-1 text-xs font-medium text-muted-foreground">{titlu}</p>
      <ul
        className="h-64 overflow-y-auto rounded border bg-background text-sm"
        role="listbox"
        aria-label={titlu}
      >
        {elemente.map((id) => (
          <li
            key={id}
            role="option"
            aria-selected={selectat === id}
            className={cn(
              "cursor-pointer px-2 py-1",
              selectat === id ? "bg-ds-blue-600 text-white" : "hover:bg-muted"
            )}
            onClick={() => onSelect(id)}
          >
            {eticheta(id)}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DialogColoane<R extends RandRaport>({
  deschis,
  toateColoanele,
  coloane,
  onInchide,
  onAplica,
}: DialogColoaneProps<R>) {
  const [afisate, setAfisate] = useState<string[]>(coloane);
  const [selAscuns, setSelAscuns] = useState<string | null>(null);
  const [selAfisat, setSelAfisat] = useState<string | null>(null);
  const eticheta = (id: string) =>
    toateColoanele.find((c) => c.id === id)?.eticheta ?? id;
  const ascunse = toateColoanele
    .map((c) => c.id)
    .filter((id) => !afisate.includes(id));

  const muta = (id: string | null, spre: "afisate" | "ascunse") => {
    if (!id) return;
    if (spre === "afisate" && !afisate.includes(id))
      setAfisate([...afisate, id]);
    if (spre === "ascunse") setAfisate(afisate.filter((x) => x !== id));
    setSelAscuns(null);
    setSelAfisat(null);
  };
  const ordoneaza = (directie: "sus" | "jos" | "inceput" | "sfarsit") => {
    if (!selAfisat) return;
    const i = afisate.indexOf(selAfisat);
    const fara = afisate.filter((x) => x !== selAfisat);
    const poz =
      directie === "inceput"
        ? 0
        : directie === "sfarsit"
          ? fara.length
          : directie === "sus"
            ? Math.max(0, i - 1)
            : Math.min(fara.length, i + 1);
    setAfisate([...fara.slice(0, poz), selAfisat, ...fara.slice(poz)]);
  };

  return (
    <Dialog open={deschis} onOpenChange={(o) => !o && onInchide()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">
            Selectează coloanele
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-3">
          <Lista
            titlu="Nu se afișează"
            elemente={ascunse}
            eticheta={eticheta}
            selectat={selAscuns}
            onSelect={setSelAscuns}
          />
          <div className="flex flex-col gap-1">
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              title="Afișează toate"
              onClick={() => setAfisate(toateColoanele.map((c) => c.id))}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              title="Afișează"
              disabled={!selAscuns}
              onClick={() => muta(selAscuns, "afisate")}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              title="Ascunde"
              disabled={!selAfisat}
              onClick={() => muta(selAfisat, "ascunse")}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              title="Ascunde toate (rămân primele două)"
              onClick={() =>
                setAfisate(toateColoanele.slice(0, 2).map((c) => c.id))
              }
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
          </div>
          <Lista
            titlu="Se afișează în raport"
            elemente={afisate}
            eticheta={eticheta}
            selectat={selAfisat}
            onSelect={setSelAfisat}
          />
          <div className="flex flex-col gap-1">
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              title="La început"
              disabled={!selAfisat}
              onClick={() => ordoneaza("inceput")}
            >
              <ChevronsUp className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              title="Mai sus"
              disabled={!selAfisat}
              onClick={() => ordoneaza("sus")}
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              title="Mai jos"
              disabled={!selAfisat}
              onClick={() => ordoneaza("jos")}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              title="La sfârșit"
              disabled={!selAfisat}
              onClick={() => ordoneaza("sfarsit")}
            >
              <ChevronsDown className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Selectează o coloană și mut-o cu săgețile; ordinea din lista din
          dreapta este ordinea din raport.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onInchide}>
            Anulează
          </Button>
          <Button
            onClick={() => onAplica(afisate)}
            disabled={afisate.length === 0}
          >
            Aplică
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
